import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Button, Spinner, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useTransferService } from "modules/Pharmacy/Transfer/api/useTransferService";
import { showToastMessage } from "../../../../common/service/CommonService";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import CommonFileProgress from "Components/Common/CommonFileUpload/CommonFileProgress";
import addVolunteerStyle from "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import { TRANSFER_PATHS } from "Routes/paths";
import FullPageLoader from "Components/Common/FullPageLoader";
import { useDispensingLog } from "modules/Pharmacy/Dispensing/api/useDispensingLog";
import { useIpInventoryService } from "modules/Pharmacy/IPinventory/api/useIpInventoryService";
import TruncatedText from "Components/Common/TruncatedText";
import { TRANSFER_ATTACHMENT_OPTIONS, TRANSFER_TYPE_OPTIONS, loadCodelistsDetailId, IP_CATEGORY_OPTIONS, getLabelFromValue } from "api/modules/common.option";

import SectionCard from "Components/Common/SectionCard";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";
import { queryClient } from "api/queryClient";
import { validations } from "validations/FormValidations";

const parseSizeToBytes = (sizeStr: any): number => {
  if (!sizeStr) return 0;
  if (typeof sizeStr === "number") return sizeStr;

  const str = String(sizeStr).toLowerCase().trim();
  const val = parseFloat(str);
  if (isNaN(val)) return 0;

  if (str.includes("gb")) return val * 1024 * 1024 * 1024;
  if (str.includes("mb")) return val * 1024 * 1024;
  if (str.includes("kb")) return val * 1024;
  return val;
};

const AddTransfer: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = paramId || searchParams.get("id");

  document.title = `${editId ? "Edit" : "New"} Drug Transfer | ZENTIXS ORBIT`;

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Drug Transfer", link: TRANSFER_PATHS.LIST },
      { text: editId ? "Edit Transfer Record" : "Add Transfer Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [activeAttachmentType, setActiveAttachmentType] = useState<null | string | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreview = (file: any) => {
 

     const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    const path = file?.filepath || file?.path;
    if (!path) return;

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const { InsertMutation, UpdateMutation, SelectIdQuery } = useTransferService();
  const selectQuery = SelectIdQuery(editId ? Number(editId) : null);

  const { DropdownQuery: studySelectAllQuery } = useDispensingLog({ getStudyDropDown: true });
  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studycode,
        ...study,
      })) || [],
    [studySelectAllQuery.data],
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    register,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      transfertype: "",
      transferfrom: "",
      transferto: "",
      fromstudyid: "",
      fromlocation: "",
      tostudyid: "",
      tolocation: "",
      studycode: "",
      ipcategory: "",
      productname: "",
      batchlotno: "",
      dosageform: "",
      strength: "",
      quantitytype: "",
      quantity: "",
      totalquantity: "",
      transferquantity: "",
      quantityremaining: "",
      remarks: "",
      selectedAttachmentTypes: [],
      attachments_list: [],
    },
    mode: "onBlur",
  });

  const transferType = watch("transfertype");

  const isStudyToStudy = useMemo(() => {
    const selectedType = TRANSFER_TYPE_OPTIONS.find((opt) => opt.value === transferType);
    return selectedType?.label?.toLowerCase().includes("study ➝ study");
  }, [transferType]);

  const selectedFromStudyId = watch(isStudyToStudy ? "transferfrom" : "studycode");
  const selectedIPCategory = watch("ipcategory");
  const selectedQuantityType = watch("quantitytype");
  const [fromstudy, setfromstudy] = useState("");
  const study = watch("transferfrom")

  useEffect(() => {
    if (transferType === "") {
      setValue("transferfrom", "")
      setValue("transferto", "")
    }
    if (isStudyToStudy) {
      setfromstudy(study)
    }
    else {
      setfromstudy("")
    }
  }, [isStudyToStudy, study, transferType]);

  useEffect(() => {
    if (fromstudy === "") {
      setValue("transferfrom", "")
      setValue("transferto", "")
    }
  }, [fromstudy]);

  const { selectedStudy: globalStudy } = useGlobalStudy();

  // Transfer studyOptions use studycode as value (not studyid), so we must
  // check presence by the spread `studyid` field instead of opt.value
  const isStudyInOptions = useMemo(() => {
    if (!globalStudy?.studyid || studyOptions.length === 0) return true;
    return studyOptions.some(
      (s: any) => String(s.studyid) === String(globalStudy.studyid)
    );
  }, [globalStudy?.studyid, studyOptions]);

  useEffect(() => {
    if (editId) return;
    if (globalStudy?.studyname && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyname)
      );
      if (matched) {
        if (isStudyToStudy) {
          setValue("transferfrom", matched.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          setValue("studycode", matched.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    } else {
      if (isStudyToStudy) {
        setValue("transferfrom", "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        setValue("studycode", "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [globalStudy?.studyname, studyOptions, editId, isStudyToStudy, setValue]);

  const { SelectIpIDQuery: ipSelectQuery } = useIpInventoryService();
  const ipQuery = ipSelectQuery(studyOptions.find((s: any) => s.value === selectedFromStudyId)?.studyid ? studyOptions.find((s: any) => s.value === selectedFromStudyId)?.studyid : null);

  const ipOptions = useMemo(
    () =>
      (ipQuery.data as any)?.data?.map((ip: any) => ({
        label: `${ip.productname} (${ip.batchlotno})`,
        value: ip.ipid,
        ...ip,
      })) || [],
    [ipQuery.data],
  );

  useEffect(() => {
    if (ipOptions.length > 0 && !watch("ipcategory")) {
      setValue("ipcategory", ipOptions[0].ipcategory);
    }
  }, [ipOptions]);

  const tostudyOptions = useMemo(
    () =>

      (studySelectAllQuery.data as any)?.data?.filter((study: any) => study.studycode !== fromstudy)?.map((study: any) => ({
        label: study.studycode,
        value: study.studycode,
        ...study,
      })) || [],
    [studySelectAllQuery.data, fromstudy],
  );

  const [isCodeListLoading, setIsCodeListLoading] = useState(true);
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsCodeListLoading(true);
        await loadCodelistsDetailId([
          "TRANSFER_TYPE_OPTIONS",
          "TRANSFER_ATTACHMENT_OPTIONS",
        ]);
      } catch (err) {
        console.error("Failed to load codelists", err);
      } finally {
        setIsCodeListLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (selectQuery.data?.data?.[0]) {
      const record = selectQuery.data.data[0];
      setExistingAttachments(record.attachments || []);
      setTotalQuantityVal(record?.totalquantity);
      reset({
        ...record,
        transfertype: record?.transfertype,
        fromstudyid: record?.fromstudyid,
        tostudyid: record?.tostudyid,
        quantitytype: record?.quantitytype,
        attachments_list: [],
      });
    }
  }, [selectQuery.data, reset]);

  // Handle IP Category Options based on from-study
  useEffect(() => {
    if (ipOptions.length > 0) {
      const uniqueCategories = Array.from(new Set(ipOptions.map((ip: any) => ip.ipcategory)));
      setIPCategoryOptions(uniqueCategories.map(cat => ({ label: cat, value: cat })));
    } else {
      setIPCategoryOptions([]);
    }
  }, [ipOptions]);

  const qtyTransferred = watch("transferquantity");

  const [totalquantityVal, setTotalQuantityVal] = useState<any>()
  const [retentionquantityVal, setRetentionQuantityVal] = useState<any>()

  // Auto-fill logic based on IP Category and Quantity Type
  useEffect(() => {
    if (!selectedFromStudyId || !selectedIPCategory) {
      if (!editId) {
        setValue("productname", "");
        setValue("batchlotno", "");
        setValue("dosageform", "");
        setValue("strength", "");
        setTotalQuantityVal(0);
        setRetentionQuantityVal(0);
      }
      return;
    }

    const matchingIP = ipOptions.find((ip: any) => ip.ipcategory === selectedIPCategory);
    if (matchingIP) {
      setValue("productname", matchingIP.productname ?? "");
      setValue("batchlotno", matchingIP.batchlotno ?? "");
      setValue("dosageform", matchingIP.dosageform ?? "");
      setValue("strength", matchingIP.strength ?? "");
      setipids(matchingIP.ipid);

      const available = Number(matchingIP.availablequantity || 0);
      setTotalQuantityVal(available);
      setRetentionQuantityVal(matchingIP.retentionquantity ?? 0);
    } else if (!editId) {
      setTotalQuantityVal(0);
      setRetentionQuantityVal(0);
    }
  }, [selectedFromStudyId, selectedIPCategory, ipOptions, setValue, editId]);

  useEffect(() => {

    if (!selectedQuantityType) {
      setValue("totalquantity", "");
      return;
    }

    const selectedType = QUANTITY_TYPE_OPTIONS.find(opt => opt.value === selectedQuantityType);
    const typeLabel = selectedType?.label?.toString().toLowerCase() || "";

    if (typeLabel.includes("actual") && typeLabel.includes("stock")) {
      setValue("totalquantity", totalquantityVal || 0);
    } else if (typeLabel.includes("retention") && typeLabel.includes("stock")) {
      setValue("totalquantity", retentionquantityVal || 0);
    } else {
      setValue("totalquantity", "");
    }
  }, [selectedQuantityType, setValue, totalquantityVal, retentionquantityVal]);

  useEffect(() => {
    const remaining = Number(watch("totalquantity") || 0) - Number(qtyTransferred || 0);
    setValue("quantityremaining", remaining >= 0 ? remaining : 0);
  }, [qtyTransferred, watch("totalquantity"), setValue]);


  const [IPCategoryOptions, setIPCategoryOptions] = useState<any[]>(IP_CATEGORY_OPTIONS);
  const [ipids, setipids] = useState();
  useEffect(() => {
    if (!ipOptions || ipOptions.length === 0) return;
    if (ipOptions.length > 0) {
      const uniqueCategories = ipOptions.map((ip: any) => ip.ipcategory);
      const updatedIPCategoryOptions = uniqueCategories.map((category: string) => ({
        label: category,
        value: category,
      }));
      setIPCategoryOptions(updatedIPCategoryOptions);
    }
  }, [ipOptions]);

  const QUANTITY_TYPE_OPTIONS = [
    { label: "Actual Stock", value: "Actual Stock" },
    { label: "Retention Stock", value: "Retention Stock" }
  ]

  const sectionAFields: FormFieldConfig[] = [
    {
      name: "transfertype",
      label: "Transfer Type",
      type: "select",
      required: true,
      disabled: !!editId,
      options: TRANSFER_TYPE_OPTIONS,
    },
    {
      name: "transferfrom",
      label: isStudyToStudy ? "From Study" : "From Location",
      type: (isStudyToStudy ? "select" : "text"),
      required: true,
      disabled: isStudyToStudy ? true : (!transferType || !!editId),
      options: isStudyToStudy ? studyOptions : undefined,
      placeholder: isStudyToStudy
        ? "Select origin study..."
        : "Enter origin location...",
    },
    {
      name: "transferto",
      label: isStudyToStudy ? "To Study" : "To Location",
      type: (isStudyToStudy ? "select" : "text") as any,
      required: true,
      disabled:
        !transferType ||
        (isStudyToStudy && (!fromstudy || !study)) ||
        !!editId,
      options: isStudyToStudy ? tostudyOptions : undefined,
      placeholder: isStudyToStudy
        ? "Select destination study..."
        : "Enter destination location...",
    },
  ];

  const sectionAFieldsRemaining: FormFieldConfig[] = [
    ...(!isStudyToStudy
      ? [
        {
          name: "studycode",
          label: "Study Code",
          type: "select" as const,
          required: true,
          options: studyOptions,
          disabled: true,
          placeholder: "Select study...",
        },
      ]
      : []),
    {
      name: "ipcategory",
      label: "IP Category",
      type: "select",
      required: true,
      options: IPCategoryOptions,
      disabled: !selectedFromStudyId || !!editId,
    },
    {
      name: "productname",
      label: "Product Name",
      type: "text",
      disabled: true,
      placeholder: "Auto-filled",
    },
    {
      name: "batchlotno",
      label: "Batch / Lot No",
      type: "text",
      disabled: true,
      placeholder: "Auto-filled",
    },
    {
      name: "dosageform",
      label: "Dosage Form",
      type: "text",
      disabled: true,
      placeholder: "Auto-filled",
    },
  ];

  const sectionCFields: FormFieldConfig[] = [
    {
      name: "quantitytype",
      label: "Quantity Type",
      type: "select",
      required: true,
      options: QUANTITY_TYPE_OPTIONS,
      disabled: !selectedIPCategory,
    },
    {
      name: "totalquantity",
      label: "Quantity Available for Transfer",
      type: "number",
      disabled: true,
    },
    {
      name: "transferquantity",
      label: "Quantity Transferred",
      type: "number",
      required: true,
      placeholder: "e.g. 150",
      disabled: !selectedQuantityType,
      rules: {
        required: "Quantity is required",
        validate: (v: any) => {
          if (Number(v) <= 0) {
            return "Quantity must be greater than 0";
          }
          if (Number(v) > Number(watch("totalquantity"))) {
            return "Cannot exceed available quantity";
          }
          return true;
        },
      },
    },
    {
      name: "quantityremaining",
      label: "Quantity Remaining",
      type: "number",
      disabled: true,
      placeholder: "Auto-calculated",
    },
  ];

  const attachmentsList = watch("attachments_list") || [];

  const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
  const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];
  const MAX_TOTAL_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

  const handleSingleFileAdd = (files: File[]) => {
    if (!activeAttachmentType) {
      showToastMessage("error", "Please select an attachment type first.");
      return;
    }

    if (files.length === 0) return;

    const file = files[0];

    // File type validation
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
      showToastMessage("error", "Only PDF, JPG, JPEG, PNG files are allowed.");
      return;
    }

    // Total size validation (existing server files + new files + this file)
    const currentList: File[] = watch("attachments_list") || [];
    const existingSizeBytes = existingAttachments.reduce((sum: number, f: any) => sum + parseSizeToBytes(f.filesize || 0), 0);
    const newSizeBytes = currentList.reduce((sum: number, f: File) => sum + f.size, 0);
    if (existingSizeBytes + newSizeBytes + file.size > MAX_TOTAL_SIZE_BYTES) {
      showToastMessage("error", "Total file size exceeds 20MB.");
      return;
    }

    Object.defineProperty(file, 'attachmenttype', {
      value: activeAttachmentType,
      writable: true,
      enumerable: true,
      configurable: true
    });

    setValue("attachments_list", [...currentList, file]);
    setActiveAttachmentType(null);
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    const formData = watch();
    const payload = {
      transferid: Number(editId),
      transfertype: formData.transfertype,
      transferfrom: formData.transferfrom,
      transferto: formData.transferto,
      ipid: ipids,
      quantitytype: formData.quantitytype,
      totalquantity: formData.totalquantity,
      transferquantity: formData.transferquantity,
      remarks: formData.remarks,
      updatereason: reasonPayload.reason,
      attachments: formData.attachments_list,
      deletedAttachmentIds: deletedAttachmentIds,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res.message || "Transfer updated successfully");
        setIsUpdateModalOpen(false);
        navigate(TRANSFER_PATHS.LIST);
      },
      onError: (err: any) => showToastMessage("error", err),
    });
  };

  const onSubmit = (data: any) => {
    if (activeAttachmentType) {
      showToastMessage(
        "error",
        "Please upload a document for the selected attachment type or cancel the current attachment selection before proceeding."
      );
      return;
    }

    const insertpayload = {
      transfertype: data.transfertype,
      transferfrom: data.transferfrom,
      transferto: data.transferto,
      ipid: ipids,
      quantitytype: data.quantitytype,
      totalquantity: data.totalquantity,
      transferquantity: data.transferquantity,
      remarks: data.remarks,
      attachments: data.attachments_list,
    };

    if (editId) {
      setIsUpdateModalOpen(true);
    } else {
      InsertMutation.mutate(insertpayload, {
        onSuccess: (res: any) => {
          showToastMessage("success", res?.message || "Transfer added successfully");
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          navigate(TRANSFER_PATHS.LIST);
        },
        onError: (err: any) => showToastMessage("error", err),
      });
    }
  };

  if (editId && isCodeListLoading && (selectQuery.isLoading || selectQuery.isFetching || ipQuery.isFetching || ipQuery.isLoading)) return <FullPageLoader />;

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-700 mb-1">Drug Transfer Execution</h3>
              <p className="text-muted mb-0">Record and manage clinical drug transfers between studies and sites.</p>
            </div>
          </div>

          {/* Study Mismatch Warning */}
          <GlobalStudyMismatchWarning
            globalStudy={globalStudy}
            studyOptions={studyOptions}
            isLoaded={!studySelectAllQuery.isLoading}
            isPresent={isStudyInOptions}
          />

          <SectionCard
            icon="ri-exchange-line"
            title="Transfer Details"
            subtitle="Specify transfer type and locations."
          >
            <CommonForm fields={sectionAFields} control={control} errors={errors} columns={3} />
            <div className="mt-3">
              <CommonForm fields={sectionAFieldsRemaining} control={control} errors={errors} columns={3} />
            </div>
          </SectionCard>

          <Row>
            <Col md={12}>
              <SectionCard
                icon="ri-scales-3-line"
                title="Quantity Movement"
                subtitle="Track quantities being moved."
              >
                <CommonForm fields={sectionCFields} control={control} errors={errors} columns={2} />
              </SectionCard>
            </Col>
          </Row>

          <SectionCard
            icon="ri-attachment-line"
            title="Documentation Attached & Remarks"
            subtitle="Select attachment types and upload the corresponding documents."
          >
            {/* 1. Styled Type Selection Chips */}
            <div className="mb-4">
              <Label className="fw-700 text-dark mb-3 ">
                Select Attachment Types
              </Label>
              <div className="d-flex flex-wrap gap-2">
                {TRANSFER_ATTACHMENT_OPTIONS.map((opt: any) => {
                  const isUploaded = existingAttachments.some(f => f.attachmenttype === opt.value) ||
                    attachmentsList.some((f: any) => (f as any).attachmenttype === opt.value);
                  const isActive = activeAttachmentType === opt.value;
                  const isDisabled = isUploaded || (activeAttachmentType !== null && !isActive);

                  return (
                    <div
                      key={opt.value}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isActive) {
                          setActiveAttachmentType(null);
                        } else {
                          setActiveAttachmentType(opt.value);
                        }
                      }}
                      style={{
                        padding: '5px 15px',
                        borderRadius: '30px',
                        border: `1.5px solid ${isActive || isUploaded ? '#2E86AB' : '#E5E7EB'}`,
                        backgroundColor: isActive || isUploaded ? '#F0F7FF' : '#FFF',
                        color: isActive || isUploaded ? '#2E86AB' : '#4B5563',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled && !isUploaded ? 0.5 : 1,
                        fontWeight: isActive || isUploaded ? '600' : '400',
                        fontSize: '13px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: isActive ? '0 4px 12px rgba(46, 134, 171, 0.15)' : 'none',
                      }}
                    >
                      <i className={`${isUploaded ? 'ri-check-line' : (isActive ? 'ri-checkbox-circle-fill' : 'ri-add-circle-line')} me-2`} style={{ fontSize: '16px' }} />
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 2. Unified Premium Dropzone */}
            <div className="mb-4">
              <Label className="fw-700 text-dark mb-2">
                Upload Documents
              </Label>

              <CommonFileProgress
                files={attachmentsList}
                existingSizeBytes={existingAttachments.reduce((sum, f) => sum + parseSizeToBytes(f.filesize || 0), 0)}
                maxSizeMB={20}
              />

              <div
                className={`rounded-4 border-dashed-[1px] text-center transition-all ${activeAttachmentType === null ? 'bg-light opacity-50' : 'bg-white'}`}
                style={{
                  border: '1px dashed ',
                  borderColor: '#9d9ea0ff',
                  padding: '40px 20px',
                  cursor: activeAttachmentType === null ? 'not-allowed' : 'pointer',
                  pointerEvents: activeAttachmentType === null ? 'none' : 'auto'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (activeAttachmentType !== null) e.currentTarget.style.backgroundColor = '#F0F7FF';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFF';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.backgroundColor = '#FFF';
                  if (activeAttachmentType === null) return;
                  const files = Array.from(e.dataTransfer.files);
                  handleSingleFileAdd(files);
                }}
                onClick={() => {
                  if (activeAttachmentType === null) return;
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = false;
                  input.onchange = (e: any) => handleSingleFileAdd(Array.from(e.target.files));
                  input.click();
                }}
              >
                <div
                  className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: '64px', height: '64px', backgroundColor: '#F0F7FF', color: '#2E86AB' }}
                >
                  <i className="ri-upload-cloud-2-line" style={{ fontSize: '32px' }} />
                </div>
                <h6 className="fw-bold mb-1">Click to upload or drag and drop</h6>
                <p className="text-muted small mb-0">
                  {activeAttachmentType === null
                    ? "Select an attachment type above to enable upload"
                    : `Upload document for ${TRANSFER_ATTACHMENT_OPTIONS.find(o => o.value === activeAttachmentType)?.label}`}
                </p>
                <p className="text-muted" style={{ fontSize: '11px' }}>Maximum file size 20 MB</p>
              </div>
            </div>

            {/* 3. File List */}
            {(existingAttachments.length > 0 || watch("attachments_list")?.length > 0) && (
              <div className="mb-4">
                {/* Existing Attachments */}
                {existingAttachments.length > 0 && (
                  <div className="mb-3">
                    <p className="text-muted small fw-bold mb-2 text-uppercase" style={{ letterSpacing: '0.5px' }}>Existing Attachments</p>
                    <Row className="g-3">
                      {existingAttachments.map((file, idx) => (
                        <Col key={`existing-${idx}`} md={4} className={addVolunteerStyle.ipInventory_wrapper}>
                          <div className={`${addVolunteerStyle.attachmentCard} h-100 shadow-sm border`}>
                            <div className={addVolunteerStyle.fileInfo}>
                              <div className={addVolunteerStyle.fileIcon}>
                                <i className="ri-file-pdf-line text-danger" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className={addVolunteerStyle.fileName}>
                                  <TruncatedText text={file.filename} />
                                </div>
                                <div className={addVolunteerStyle.fileMeta}>
                                  {getLabelFromValue(TRANSFER_ATTACHMENT_OPTIONS, file.attachmenttype, "Document")}
                                </div>
                              </div>
                            </div>
                            <div className={addVolunteerStyle.fileActions}>
                              <i
                                className="ri-eye-line text-primary"
                                title="Preview"
                                style={{ cursor: "pointer" }}
                                onClick={() => handlePreview(file)}
                              />
                              <i
                                className="ri-delete-bin-line text-danger"
                                title="Delete"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setDeletedAttachmentIds(prev => [...prev, file.transferattachmentid]);
                                  setExistingAttachments(prev => prev.filter(a => a.transferattachmentid !== file.transferattachmentid));
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* New Attachments */}
                {watch("attachments_list")?.length > 0 && (
                  <div>
                    <p className="text-muted small fw-bold mb-2 text-uppercase" style={{ letterSpacing: '0.5px' }}>New Attachments</p>
                    <Row className="g-3">
                      {watch("attachments_list").map((file: File, index: number) => (
                        <Col key={`new-${index}`} md={4} className={addVolunteerStyle.ipInventory_wrapper}>
                          <div className={`${addVolunteerStyle.attachmentCard} h-100 shadow-sm border`}>
                            <div className={addVolunteerStyle.fileInfo}>
                              <div className={addVolunteerStyle.fileIcon}>
                                <i className="ri-file-text-line text-primary" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className={addVolunteerStyle.fileName}>
                                  <TruncatedText text={file.name} />
                                </div>
                                <div className={addVolunteerStyle.fileMeta}>
                                  {(file.size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                            </div>
                            <div className={addVolunteerStyle.fileActions}>
                              <i
                                className="ri-eye-line text-primary"
                                title="Preview"
                                style={{ cursor: "pointer" }}
                                onClick={() => handlePreview(file)}
                              />
                              <i
                                className="ri-delete-bin-line text-danger"
                                title="Remove"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  const list = [...watch("attachments_list")];
                                  list.splice(index, 1);
                                  setValue("attachments_list", list);
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </div>
            )}

            <Row>
              <Col md={12} className="mt-2">
                <FormGroup>
                  <Label className="fw-700 text-dark mb-2 ">Remarks</Label>
                  <Controller
                    name="remarks"
                    control={control}
                    rules={validations.transfer.remarks}
                    render={({ field }) => (
                      <Input
                        type="textarea"
                        {...field}
                        rows={3}
                        maxLength={251}
                        className="form-control"
                        placeholder="Enter any additional notes..."
                        style={{ backgroundColor: 'white' }}
                      />
                    )}
                  />
                  {errors.remarks?.message && (
                    <div className="text-danger small mt-1">
                      {String(errors.remarks.message)}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button
                type="button"
                color="dark"
                outline
                onClick={() => navigate(TRANSFER_PATHS.LIST)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                outline
                disabled={InsertMutation.isPending || UpdateMutation.isPending}
              >
                {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
                {editId ? "Update" : "Submit"}
              </Button>
            </div>
          </div>
        </form>

        {isUpdateModalOpen && (
          <Reason
            isOpen={isUpdateModalOpen}
            toggle={() => setIsUpdateModalOpen(false)}
            click={handleUpdate}
            action="Update"
            id={editId ? Number(editId) : null}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {previewOpen && (
          <FilePreviewModal
            isOpen={previewOpen}
            onClose={() => setPreviewOpen(!previewOpen)}
            file={previewFile}
            fileUrl={previewFileUrl}
          />
        )}
      </Container>
    </div>
  );
};

export default AddTransfer;
